import * as React from 'react';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { Thanks } from '../../../layout/Thanks';

import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';

import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { AboutValuationText } from '../../../partials/AboutValuationText';
import { PageType } from '../../../../__types__/generated/globalTypes';
import { useValuationContext } from '../../../../utils/hooks/useValuationContext';
import { ValuationForm } from '../../../partials/forms/horizontal/ValuationWithoutConsent';
import { IUseFormField } from '../../../../utils/hooks/useForm';

const ValuationGeneral3: React.FC = () => {
  const { posted, init } = useValuationContext();

  React.useEffect(() => {
    const fields: IUseFormField[] = [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse (Valgfri)'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer',
        filter: 'zip',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 87654321',
        value: '',
        label: 'Telefonnummer',
        //labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post',
        //labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ];
    init({ pageType: PageType.GENERELL }, fields);
  }, []);

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  // Lager et referanse punkt som html element
  const containerRef = React.useRef<HTMLDivElement>(null);

  //Laster inn script etter hovedinnholdet / Container på siden er lastet inn
  React.useEffect(() => {
    // Hvs Container komponent finnes
    if (containerRef.current) {
      // Last inn vev script
      const script = document.createElement('script');
      script.src = 'https://embed.vev.page/v1/VjeUhIp3xh/p5et88GBCcy';
      // Legger til scriptet rett etter Container komponenten
      containerRef?.current?.parentNode!.insertBefore(
        script,
        containerRef?.current?.nextSibling
      );

      return () => {
        // Fjerner scriptet når komponenten blir unmountet
        containerRef?.current?.parentNode!.removeChild(script);
      };
    }
  }, []);

  return (
    <>
      <Seo
        title="Salg av eiendom? - PrivatMegleren"
        description="Verdivurdering av eiendom, leilighet, hus,  estimert eiendomsverdi, E-takst. Kontakt PrivatMegleren."
        imageTitle="/verdivurdering-eiendom-eiendomsmegler-E-takst-salg-av-eiendom-leilighet-hus"
      />
      <Container className="padding-top" style={pageFadeIn} ref={containerRef}>
        <FormWrapper style={fadeOut}>
          <StyledContent>
            <Section className="form-section first">
              <Heading tag="h1" center={true}>
                Skal du selge eiendom eller bolig?
              </Heading>
              <Paragraph className="form-text">
                Forbrukerrådet anbefaler boligeiere å vurdere flere meglere når
                boligen skal selges. Det gjør vi også. Ved å sammenligne
                meglere, vil du oppleve forskjellen.
              </Paragraph>
              <Paragraph
                className="form-text"
                style={{ marginBottom: '0.5em' }}
              >
                Når du velger PrivatMegleren får du,
              </Paragraph>
              <List>
                <li>
                  Eksperter ved din side - Arbeid med noen av Norges ledende
                  eiendomsmeglere.
                </li>
                <li>
                  Skreddersøm for hvert salg - Vi tilpasser salgsprosessen for å
                  overgå dine forventninger.
                </li>
                <li>
                  Innovative verktøy - Tilgang til avanserte verktøy som
                  forenkler salgsprosessen.
                </li>
              </List>
            </Section>
            <Section className="form-section second">
              <Title className="form-text">Klar for en verdivurdering?</Title>
              <Paragraph className="form-text last-item">
                Prosessen er enkel. Fyll ut skjemaet, og en av våre meglere tar
                kontakt for en uforpliktende prat.
              </Paragraph>
              <ValuationForm />
            </Section>
          </StyledContent>
          <Section className="form-section">
            <AboutValuationText />
          </Section>
          <Section className="form-section">
            <AccordionBenefits />
          </Section>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg så snart som mulig.
            </Paragraph>
            <Paragraph center={true}>
              I mellomtiden kan du lese mer om salg av eiendom her.
            </Paragraph>
            <AccordionGeneral />
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
      </Container>
      <BackgroundLoad
        opacity={0.3}
        url="https://cdn.reeltime.no/pm_assets/img/verdivurdering.jpg"
      />
    </>
  );
};

export default ValuationGeneral3;

const FormWrapper = styled(animated.div)``;

const Title = styled(Paragraph)`
  font-family: 'DomaineText-Regular', 'Times New Roman', serif;
  font-size: 24px;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .first {
    padding: 3.125rem 0 0 0;
  }

  .second {
    padding: 0;

    .last-item {
      margin-bottom: 24px;
    }
  }

  @media all and (min-width: 800px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2em;

    &.center {
      align-items: center;
    }

    .second {
      padding-top: 3.125rem;
    }
  }
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  li {
    list-style-position: inside;
    &::before {
      content: '';
      margin-left: -5px;
    }
    &:not(:last-child) {
      padding-bottom: 0.5em;
    }
  }
`;
